import React, { useCallback, useEffect, useState } from "react";
import { Form, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faChevronLeft,
	faChevronRight,
	faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import {
	hybridGetGameLogs,
	HybridGameLogResponseData,
	HybridGameLogSource,
} from "../../services/hybridPlayer.service";

const PAGE_SIZE = 20;
const MAX_LOOKBACK_DAYS = 60;

const pad = (n: number) => String(n).padStart(2, "0");
const toDateInput = (d: Date) =>
	`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const formatAmount = (value: number) =>
	Number(value || 0).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

/** "2026-10-26 14:03:12" → "14:03:12" (the date is already in the filter) */
const formatTime = (datetime: string) => {
	const parts = String(datetime || "").split(" ");
	return parts[1] || datetime;
};

interface Props {
	/** Load only while the tab is visible */
	active: boolean;
}

/**
 * Hybrid player "Game Logs" tab: seamless and transfer bets for one day,
 * merged newest first, with the same valid-bet figure used for turnover.
 */
const HybridGameLogTab: React.FC<Props> = ({ active }) => {
	const { t } = useTranslation();
	const today = toDateInput(new Date());
	const minDate = toDateInput(
		new Date(Date.now() - MAX_LOOKBACK_DAYS * 24 * 60 * 60 * 1000)
	);

	const [date, setDate] = useState(today);
	const [source, setSource] = useState<HybridGameLogSource>("ALL");
	const [providerId, setProviderId] = useState<number | null>(null);
	const [current, setCurrent] = useState(1);
	const [data, setData] = useState<HybridGameLogResponseData | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const result: any = await hybridGetGameLogs({
				date,
				source,
				provider_id: providerId,
				current,
				pageSize: PAGE_SIZE,
			});
			if (!result?.status) {
				setData(null);
				setError(result?.message || t("Unable to load game logs"));
				return;
			}
			setData(result.data as HybridGameLogResponseData);
		} catch (e) {
			setData(null);
			setError(t("Unable to load game logs"));
		} finally {
			setLoading(false);
		}
	}, [date, source, providerId, current, t]);

	useEffect(() => {
		if (active) load();
	}, [active, load]);

	const total = data?.total || 0;
	const maxPage = Math.max(
		1,
		Math.ceil(Math.min(total, data?.max_rows || total) / PAGE_SIZE)
	);
	const summary = data?.summary?.all;

	const resetPageAnd = (fn: () => void) => {
		fn();
		setCurrent(1);
	};

	return (
		<div className="hybrid-game-log">
			<div className="hybrid-game-log__filters">
				<Form.Control
					type="date"
					size="sm"
					value={date}
					min={minDate}
					max={today}
					onChange={(e) =>
						e.target.value && resetPageAnd(() => setDate(e.target.value))
					}
					aria-label={t("Date")}
				/>
				<Form.Select
					size="sm"
					value={source}
					onChange={(e) =>
						resetPageAnd(() =>
							setSource(e.target.value as HybridGameLogSource)
						)
					}
					aria-label={t("Type")}
				>
					<option value="ALL">{t("All")}</option>
					<option value="SEAMLESS">{t("Seamless")}</option>
					<option value="TRANSFER">{t("Transfer")}</option>
				</Form.Select>
				<Form.Select
					size="sm"
					value={providerId ?? ""}
					onChange={(e) =>
						resetPageAnd(() =>
							setProviderId(e.target.value ? Number(e.target.value) : null)
						)
					}
					aria-label={t("Provider")}
				>
					<option value="">{t("All Providers")}</option>
					{(data?.providers || []).map((p) => (
						<option key={p.provider_id} value={p.provider_id}>
							{p.provider_name}
						</option>
					))}
					{providerId &&
						!(data?.providers || []).some(
							(p) => p.provider_id === providerId
						) && <option value={providerId}>#{providerId}</option>}
				</Form.Select>
				<button
					type="button"
					className="hybrid-game-log__refresh"
					onClick={load}
					disabled={loading}
					aria-label={t("Refresh")}
				>
					<FontAwesomeIcon icon={faRotateRight} spin={loading} />
				</button>
			</div>

			<div className="hybrid-game-log__summary">
				<div>
					<span>{t("Bets")}</span>
					<strong>{summary?.count ?? 0}</strong>
				</div>
				<div>
					<span>{t("Total Bet")}</span>
					<strong>{formatAmount(summary?.bet_amount ?? 0)}</strong>
				</div>
				<div>
					<span>{t("Total Win")}</span>
					<strong>{formatAmount(summary?.win_amount ?? 0)}</strong>
				</div>
				<div>
					<span>{t("Valid Bet")}</span>
					<strong>{formatAmount(summary?.valid_bet ?? 0)}</strong>
				</div>
			</div>

			{data?.unavailable_sources?.length ? (
				<p className="hybrid-game-log__note">
					{t(
						"Some game logs are temporarily unavailable, please try again later."
					)}
				</p>
			) : null}

			<div className="history-table-container">
				<Table striped bordered hover size="sm" responsive>
					<thead>
						<tr>
							<th>{t("Time")}</th>
							<th>{t("Provider")}</th>
							<th>{t("Game")}</th>
							<th>{t("Type")}</th>
							<th className="text-end">{t("Bet")}</th>
							<th className="text-end">{t("Win")}</th>
							<th className="text-end">{t("Valid Bet")}</th>
						</tr>
					</thead>
					<tbody>
						{loading && !data ? (
							<tr>
								<td colSpan={7} className="text-center">
									<Spinner animation="border" size="sm" />
								</td>
							</tr>
						) : error ? (
							<tr>
								<td colSpan={7} className="text-center">
									{error}
								</td>
							</tr>
						) : !data?.list?.length ? (
							<tr>
								<td colSpan={7} className="text-center">
									{t("No game logs for this day")}
								</td>
							</tr>
						) : (
							data.list.map((row) => (
								<tr key={row.key}>
									<td>{formatTime(row.bet_datetime)}</td>
									<td>{row.provider_name}</td>
									<td>{row.game_name || "-"}</td>
									<td>
										<span
											className={`hybrid-game-log__badge hybrid-game-log__badge--${row.source.toLowerCase()}`}
										>
											{row.source === "SEAMLESS"
												? t("Seamless")
												: t("Transfer")}
										</span>
										{row.status && row.status !== "COMPLETED" && (
											<small className="hybrid-game-log__status">
												{t(row.status)}
											</small>
										)}
									</td>
									<td className="text-end">{formatAmount(row.bet_amount)}</td>
									<td className="text-end">{formatAmount(row.win_amount)}</td>
									<td className="text-end">{formatAmount(row.valid_bet)}</td>
								</tr>
							))
						)}
					</tbody>
				</Table>
			</div>

			{total > PAGE_SIZE && (
				<div className="hybrid-game-log__pager">
					<button
						type="button"
						disabled={loading || current <= 1}
						onClick={() => setCurrent((c) => Math.max(1, c - 1))}
						aria-label={t("Previous")}
					>
						<FontAwesomeIcon icon={faChevronLeft} />
					</button>
					<span>
						{current} / {maxPage}
					</span>
					<button
						type="button"
						disabled={loading || current >= maxPage}
						onClick={() => setCurrent((c) => Math.min(maxPage, c + 1))}
						aria-label={t("Next")}
					>
						<FontAwesomeIcon icon={faChevronRight} />
					</button>
				</div>
			)}
			{total > (data?.max_rows || Infinity) && (
				<p className="hybrid-game-log__note">
					{t("Showing the latest {{count}} bets. Filter by provider to see more.", {
						count: data?.max_rows,
					})}
				</p>
			)}
		</div>
	);
};

export default HybridGameLogTab;
